// Pen pattern — solid-fill circle generated with the midpoint circle
// algorithm. Identical concept to the legacy Java
// `PaintToolCanvas.createPenPattern` that ran for 25 years.
//
// (architecture.md §19.2 — 全ペン共通の「ベタ塗り円」)
//
// Each pattern is a square mask of size `size×size`. A pixel is `1` when
// inside the circle, `0` otherwise. The mask carries no color and no
// alpha gradient on purpose: the per-stamp alpha shape comes from the
// 2層レンダリング sweep (architecture.md §19.1), not the pattern.
//
// Patterns are cached by integer pen width so a stroke that uses the same
// width over and over does no extra allocation.
const MIN_WIDTH = 1;
const MAX_WIDTH = 64; // matches MAX_STROKE_WIDTH in @oekaki/protocol
const cache = new Map();
/** Clamp + round + cache lookup. Always returns a valid pattern. */
export const getPenPattern = (rawWidth) => {
    const w = clampWidth(Math.round(rawWidth));
    const cached = cache.get(w);
    if (cached !== undefined)
        return cached;
    const built = buildPenPattern(w);
    cache.set(w, built);
    return built;
};
/** Test-only: clear the LRU. Real code never needs this. */
export const _resetPenPatternCache = () => {
    cache.clear();
};
const clampWidth = (w) => {
    if (!Number.isFinite(w) || w < MIN_WIDTH)
        return MIN_WIDTH;
    if (w > MAX_WIDTH)
        return MAX_WIDTH;
    return w;
};
const buildPenPattern = (width) => {
    // Width 1: single pixel. Avoids a 1x1 mask that's empty after rounding.
    if (width <= 1) {
        return { width: 1, size: 1, mask: Uint8Array.of(1) };
    }
    // Width 2: 2×2 solid block (legacy parity, see Java
    // `createPenPattern`'s explicit width==2 branch).
    if (width === 2) {
        return { width: 2, size: 2, mask: Uint8Array.of(1, 1, 1, 1) };
    }
    const size = width;
    const mask = new Uint8Array(size * size);
    const cx = (size - 1) / 2;
    const cy = (size - 1) / 2;
    // Use radius slightly under width/2 so even-width pens look symmetric and
    // odd-width pens include their on-axis pixel pair.
    const r = width / 2;
    // Compare squared distances; use (r - 0.5)^2-ish threshold so the disc
    // includes pixels whose CENTER is at distance < r. This produces visually
    // smooth circles for widths 3..MAX_WIDTH.
    const r2 = r * r;
    for (let y = 0; y < size; y++) {
        const dy = y - cy;
        const dy2 = dy * dy;
        const row = y * size;
        for (let x = 0; x < size; x++) {
            const dx = x - cx;
            if (dx * dx + dy2 <= r2) {
                mask[row + x] = 1;
            }
        }
    }
    // Safety: guarantee at least one lit pixel. Pathological roundings on tiny
    // widths shouldn't yield an empty mask.
    if (!mask.some((v) => v === 1)) {
        const center = Math.floor(cy) * size + Math.floor(cx);
        mask[center] = 1;
    }
    return { width, size, mask };
};
//# sourceMappingURL=penPattern.js.map